import apiClient from './apiClient';
import { ApiResponse, PaginatedResponse, PaginationParams } from './types';

export class BaseService<T, CreateT = Partial<T>, UpdateT = Partial<T>> {
  protected endpoint: string;

  constructor(endpoint: string) {
    this.endpoint = endpoint;
  }

  // Get all items with optional pagination
  async getAll(params?: PaginationParams): Promise<T[]> {
    const response = await apiClient.get<ApiResponse<T[]>>(this.endpoint, {
      params
    });

    // Handle different response structures
    if (response.data?.data) {
      return response.data.data;
    } else if (Array.isArray(response.data)) {
      return response.data;
    } else {
      console.warn('Unexpected API response structure:', response.data);
      return [];
    }
  }

  // Get paginated items
  async getPaginated(params: PaginationParams): Promise<PaginatedResponse<T>> {
    const response = await apiClient.get<ApiResponse<PaginatedResponse<T>>>(
      `${this.endpoint}/paginated`,
      { params }
    );
    return response.data.data;
  }

  // Get item by ID
  async getById(id: string): Promise<T> {
    const response = await apiClient.get<ApiResponse<T>>(
      `${this.endpoint}/${id}`
    );

    // Handle different response structures
    if (response.data?.data) {
      return response.data.data;
    } else if (response.data && typeof response.data === 'object') {
      return response.data as T;
    } else {
      console.warn(
        'Unexpected API response structure for getById:',
        response.data
      );
      throw new Error('Invalid response structure');
    }
  }

  // Create new item
  async create(data: CreateT): Promise<T> {
    const response = await apiClient.post<ApiResponse<T>>(this.endpoint, data);

    // Handle different response structures
    if (response.data?.data) {
      return response.data.data;
    } else if (response.data && typeof response.data === 'object') {
      return response.data as T;
    } else {
      console.warn(
        'Unexpected API response structure for create:',
        response.data
      );
      throw new Error('Invalid response structure');
    }
  }

  // Update existing item
  async update(id: string, data: UpdateT): Promise<T> {
    const response = await apiClient.put<ApiResponse<T>>(
      `${this.endpoint}/${id}`,
      data
    );

    // Handle different response structures
    if (response.data?.data) {
      return response.data.data;
    } else if (response.data && typeof response.data === 'object') {
      return response.data as T;
    } else {
      console.warn(
        'Unexpected API response structure for update:',
        response.data
      );
      throw new Error('Invalid response structure');
    }
  }

  // Partially update existing item
  async patch(id: string, data: Partial<UpdateT>): Promise<T> {
    const response = await apiClient.patch<ApiResponse<T>>(
      `${this.endpoint}/${id}`,
      data
    );

    // Handle different response structures
    if (response.data?.data) {
      return response.data.data;
    } else if (response.data && typeof response.data === 'object') {
      return response.data as T;
    } else {
      console.warn(
        'Unexpected API response structure for patch:',
        response.data
      );
      throw new Error('Invalid response structure');
    }
  }

  // Delete item by ID
  async delete(id: string): Promise<void> {
    await apiClient.delete(`${this.endpoint}/${id}`);
  }

  // Search items
  async search(query: string, filters?: Record<string, unknown>): Promise<T[]> {
    const response = await apiClient.get<ApiResponse<T[]>>(
      `${this.endpoint}/search`,
      {
        params: { q: query, ...filters }
      }
    );

    // Handle different response structures
    if (response.data?.data) {
      return response.data.data;
    } else if (Array.isArray(response.data)) {
      return response.data;
    } else {
      console.warn(
        'Unexpected API response structure for search:',
        response.data
      );
      return [];
    }
  }
}
